import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";

import CustomButton from "../../Components/CustomButton";
import CustomModal from "../../Components/CustomModal";
import ReviewProcessModal from "../../Components/ReviewProcessModal";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import Loader from '../../Components/Loader';
import CustomTable from "./../../Components/CustomTable";

import "./style.css";

import FormModal from "../../Components/FormModal";
import Header from "../../Components/Header";
import ProfileDisplay from "../../Components/ProfileDisplay";
import usePagination from "../../Hooks/usePagination";
import useSearch from "../../Hooks/useSearch";
import useToggle from "../../Hooks/useToggle";
import { useGetUsersQuery, useHandleStatusMutation } from "../../Redux/Apis/User";
import { useSendLoginLinkMutation } from "../../Redux/Apis/Admin";
import { toast } from "react-toastify";
import { dateFormatter } from "../../Utils";

const sortValues = [
  {
    text: "Name",
    value: "fullName",
  },
  {
    text: "Email",
    value: "email",
  }
];

const perPageValues = [
  {
    text: "10",
    value: 10,
  },
  {
    text: "20",
    value: 20,
  },
  {
    text: "50",
    value: 50,
  }
];

export const ResidentApprovals = () => {

  const [handleStatus, { isLoading: isUpdating }] = useHandleStatusMutation();
  const [sendLoginLink, { isLoading: isSendingLink }] = useSendLoginLinkMutation();

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showApproveSuccessModal, setShowApproveSuccessModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRejectSuccessModal, setShowRejectSuccessModal] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [showProcess, setShowProcess] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState();
  const [showSendLinkModal, setShowSendLinkModal] = useState(false);
  const [showSendLinkSuccessModal, setShowSendLinkSuccessModal] = useState(false);
  const [selectedUserForLink, setSelectedUserForLink] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [sortBy, setSortBy] = useState(sortValues[0].value);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [formOpen, setFormOpen, toggleForm] = useToggle();
  const [selectedData, setSelectedData] = useState();
  const [search, debouncedSearch, onSearch] = useSearch();
  const { page, perPage, onNext, onPrevious } = usePagination();

  // Fetch pending users (regular residents, not vendors)
  const { data, isLoading, isFetching, refetch } = useGetUsersQuery({
    page,
    rowsPerPage: perPage,
    role: "user",  // Regular users/residents
    status: "pending",  // Only pending approval
    search: debouncedSearch,
    sortBy,
    from,
    to,
  });

  const toggleFilter = () => setIsFilterOpen(!isFilterOpen);

  const handleReject = async () => {
    setShowRejectModal(false);
    setReasonOpen(true);
  };

  const handleApprove = async () => {
    await handleStatus({ id: selectedUserId, payload: { status: "approved" } });
    refetch();
    setShowApproveModal(false);
    setShowApproveSuccessModal(true);
  };

  const onConfirmReject = async (data) => {
    await handleStatus({ id: selectedUserId, payload: { status: "rejected", ...data } }).then(res => {
      if (!res?.error) {
        refetch();
        setReasonOpen(false);
        setShowRejectSuccessModal(true);
      }
    }).catch(err => {
      console.log("err", err);
    });
  };

  // Send a one-time login link to the resident's email so they can sign in
  // without needing to remember a password (useful for newly approved residents).
  const handleSendLoginLink = async () => {
    if (!selectedUserForLink) return;
    try {
      await sendLoginLink(selectedUserForLink._id).unwrap();
      setShowSendLinkModal(false);
      setShowSendLinkSuccessModal(true);
      setSelectedUserForLink(null);
    } catch (e) {
      toast.error(e?.data?.message || "Failed to send login code. Please try again.");
      setShowSendLinkModal(false);
    }
  };

  useEffect(() => {
    document.title = 'The Turn At | Resident Approvals';
  }, []);

  const headers = [
    {
      key: "id",
      title: "S.No",
    },
    {
      key: "name",
      title: "Resident",
    },
    {
      key: "email",
      title: "Email",
    },
    {
      key: "createdAt",
      title: "Signup Date",
    },
    {
      key: "status",
      title: "Status",
    },
    {
      key: "actions",
      title: "Actions",
    },
    {
      key: "loginCode",
      title: "Login",
    },
  ];

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <div className="dashCard">
                <Header
                  title="Resident Approval Requests"
                  search={search}
                  onSearch={onSearch}
                  buttons={[
                    {
                      label: "View Review Process",
                      variant: "secondaryButton",
                      onClick: () => setShowProcess(true),
                    },
                  ]}
                />

                {/* Info banner */}
                <div className="alert alert-info mb-3">
                  <strong>Note:</strong> Only approve residents who you have verified live in your neighborhood.
                  This helps maintain the security and integrity of your community.
                </div>

                <div className="row mb-3">
                  {
                    (isLoading || isFetching) ? <Loader /> :
                      <div className="col-12">
                        {data?.data?.length === 0 ? (
                          <div className="text-center py-5">
                            <h4 className="text-muted">No pending resident approvals</h4>
                            <p className="text-muted">All residents have been reviewed!</p>
                          </div>
                        ) : (
                          <CustomTable
                            headers={headers}
                            isFilterOpen={isFilterOpen}
                            dateFilter={false}
                            filterSort={false}
                            perPage={true}
                            filterSortValues={sortValues}
                            perPageValues={perPageValues}
                            filterSortValue={sortBy}
                            setFilterSortValue={setSortBy}
                            filterFrom={from}
                            setFilterFrom={setFrom}
                            filterTo={to}
                            setFilterTo={setTo}
                            length={data?.data?.length}
                            page={page}
                            rowsPerPage={perPage}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            total={data?.total}
                          >
                            <tbody>
                              {data?.data?.map((item, index) => (
                                <tr key={item._id}>
                                  <td>{(index + 1) + ((page - 1) * perPage)}</td>
                                  <td>
                                    <ProfileDisplay
                                      name={`${item?.name}`}
                                      image={item?.picture}
                                    />
                                  </td>
                                  <td>{item?.email}</td>
                                  <td>{dateFormatter(item?.createdAt)}</td>
                                  <td>
                                    <span className="badge bg-warning text-dark">
                                      Pending
                                    </span>
                                  </td>
                                  <td>
                                    <div className="d-flex flex-column gap-1 residentActions">
                                      <CustomButton
                                        variant="successButton"
                                        text="Approve"
                                        onClick={() => {
                                          setSelectedUserId(item._id);
                                          setShowApproveModal(true);
                                        }}
                                      />
                                      <CustomButton
                                        variant="dangerButton"
                                        text="Reject"
                                        onClick={() => {
                                          setSelectedUserId(item._id);
                                          setShowRejectModal(true);
                                        }}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    {/* Send a magic login link to this resident's email */}
                                    <CustomButton
                                      variant="secondaryButton"
                                      text="Send Login Code"
                                      className="btn-sm"
                                      onClick={() => {
                                        setSelectedUserForLink(item);
                                        setShowSendLinkModal(true);
                                      }}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </CustomTable>
                        )}
                      </div>
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Approve Confirmation Modal */}
          <CustomModal
            loading={isUpdating}
            show={showApproveModal}
            close={() => { setShowApproveModal(false); }}
            action={handleApprove}
            heading='Are you sure you want to approve this resident?'
          />
          <CustomModal
            show={showApproveSuccessModal}
            close={() => { setShowApproveSuccessModal(false); }}
            success
            heading='Resident Approved Successfully!'
          />

          {/* Reject Confirmation Modal */}
          <CustomModal
            loading={isUpdating}
            show={showRejectModal}
            close={() => { setShowRejectModal(false); }}
            action={handleReject}
            heading='Are you sure you want to reject this resident?'
          />
          <CustomModal
            show={showRejectSuccessModal}
            close={() => { setShowRejectSuccessModal(false); }}
            success
            heading='Resident Rejected'
          />

          <ReviewProcessModal type="resident" show={showProcess} onClose={() => setShowProcess(false)} />

          {/* Send Login Code — confirm before emailing a magic link */}
          <CustomModal
            loading={isSendingLink}
            show={showSendLinkModal}
            close={() => {
              setShowSendLinkModal(false);
              setSelectedUserForLink(null);
            }}
            action={handleSendLoginLink}
            heading={`Send a one-time login code to ${selectedUserForLink?.name || 'this resident'}? They'll receive it by email.`}
          />
          <CustomModal
            show={showSendLinkSuccessModal}
            close={() => setShowSendLinkSuccessModal(false)}
            success
            heading="Login code sent to their email."
          />

          {/* Rejection Reason Form */}
          <FormModal
            title={`Reject Resident`}
            open={reasonOpen}
            toggle={() => setReasonOpen(!reasonOpen)}
            onSubmit={onConfirmReject}
            inputs={[
              {
                id: "reason",
                label: "Reason for Rejection",
                placeholder: "Enter the reason for rejecting this resident (e.g., could not verify residence)",
                required: true,
                initialValue: "",
                type: "textarea"
              }
            ]}
          />

        </div>
      </DashboardLayout>
    </>
  );
};

export default ResidentApprovals;

